import React, { useMemo, useRef, useState } from 'react';
import SwipeableViews from 'react-swipeable-views';
import { virtualize } from 'react-swipeable-views-utils';

import { css, Global } from '@emotion/react';
import { Box, Button, Icon, Image } from '@opendoor/bricks/core';
import { colors } from '@opendoor/bricks/theme/eero';
import ImgixImg from '@opendoor/bricks/utils/ImgixImg';
import { range } from 'lodash-es';

import { MlsWatermarkLogoHelper } from 'shared/helpers/MlsWatermarkLogoHelper';

import { PropertyImageCarouselProgress } from 'buying/components/propertyCards/PropertyImageCarouselProgress';

interface CarouselArrowProps {
  side: 'left' | 'right';
  onChangeIndex: (e: React.MouseEvent) => void;
  hover?: boolean;
}

const CarouselArrow: React.FC<CarouselArrowProps> = ({ side, onChangeIndex, hover = false }) => {
  const iconName = side === 'right' ? 'chevron-right' : 'chevron-left';
  const srOnly = hover
    ? {}
    : {
        left: '-9999px',
        width: '1px',
        height: '1px',
        overflow: 'hidden',
        __css: {
          clip: 'rect(1px, 1px, 1px, 1px)',
        },
      };
  return (
    <Button
      analyticsName={`property-carousel-arrow-${side}`}
      variant="ghost"
      position="absolute"
      left={side === 'left' ? 0 : undefined}
      right={side === 'right' ? 0 : undefined}
      top={0}
      overflow={hover ? undefined : 'hidden'}
      opacity={hover ? 1 : 0}
      alignItems="center"
      height="100%"
      onClick={(e) => onChangeIndex(e)}
      data-testid={`${side}-image-carousel`}
      background={`linear-gradient(${
        side === 'right' ? '270deg' : '90deg'
      }, rgba(34, 34, 34, 0.3) 0%, rgba(34, 34, 34, 0) 100%)`}
      sx={{
        ' & > *': {
          _hover: { backgroundColor: 'none' },
          _disabled: { backgroundColor: 'none' },
        },
      }}
      border="none"
      transition="opacity 0.3s ease-in-out"
      tabIndex={0}
      aria-label={`Move to the ${side === 'left' ? 'previous' : 'next'} photo in the carousel`}
      className={hover ? '' : 'sr-only'}
      id={`arrow-${side}`}
      {...srOnly}
    >
      <Box
        pl={side === 'left' ? 4 : undefined}
        pr={side === 'right' ? 4 : undefined}
        opacity={0.8}
        _hover={{
          opacity: 1,
        }}
        transition="opacity 0.2s ease-in-out"
      >
        <Icon name={iconName} color="neutrals0"></Icon>
      </Box>
    </Button>
  );
};

interface MobileCarouselProps {
  photos: Array<string>;
  photoCount: number;
}
/** Use this component if you need mobile swiping in the future (without the carousel) */
export const MobileCarousel: React.FC<MobileCarouselProps> = ({ photos, photoCount }) => {
  const VirtualizeSwipeableViews = virtualize(SwipeableViews);
  const persistedIndex = useRef(0);
  const [currentIndexState, setCurrentIndexState] = useState(persistedIndex.current);

  /** handles swipe..... */
  const handleSwipe = (index: number) => {
    const currentIndex = ((index % photos.length) + photos.length) % photos.length;
    persistedIndex.current = currentIndex;
    setCurrentIndexState(currentIndex);
  };

  /** Render function to be used by the VirtualizeSwipeableViews component */
  const slideRenderer = ({ key, index }: { key: number; index: number }) => {
    const currentIndex = ((index % photos.length) + photos.length) % photos.length;
    return (
      <Box height="100%" position="relative" key={key.toString()}>
        <Image
          src={photos[currentIndex]}
          alt=""
          borderBottomLeftRadius="none"
          borderBottomRightRadius="none"
          width="100%"
          height="100%"
          data-testid={`${currentIndex}-image-carousel`}
          objectFit="cover"
        />
      </Box>
    );
  };
  return (
    <Box position="relative">
      <VirtualizeSwipeableViews
        slideRenderer={slideRenderer}
        index={currentIndexState}
        onChangeIndex={handleSwipe}
        enableMouseEvents
      />
      <PropertyImageCarouselProgress currentIndex={currentIndexState} photoCount={photoCount} />
    </Box>
  );
};

export interface PropertyImageCarouselProps {
  mlsName?: string;
  photos: Array<string>;
  photoCount: number;
  propertyAddress: string;
}

/**
 * Carousel of images for PropertyCard
 *
 * storybook-check-ignore
 */
export const PropertyImageCarousel: React.FC<PropertyImageCarouselProps> = ({
  mlsName,
  photos,
  photoCount,
  propertyAddress,
}) => {
  const persistedIndex = useRef(0);
  const [currentIndex, setCurrentIndex] = useState(persistedIndex.current);
  const [shouldShowArrow, setShouldShowArrow] = useState(false);

  // Eager load the first photo and, after hover, the next 2 photos
  const [shouldEagerLoad, setShouldEagerLoad] = useState(false);
  const photoIndexesToEagerLoad = range(currentIndex, currentIndex + (shouldEagerLoad ? 3 : 1));

  /** Handles index change on swiping */
  const handleChangeIndex = (side: string, e: React.MouseEvent) => {
    e.preventDefault();
    const imagesLength = photos.length;
    const indexDir = side === 'left' ? -1 : 1;
    const nextIndex = (((currentIndex + indexDir) % imagesLength) + imagesLength) % imagesLength;
    persistedIndex.current = nextIndex;
    setCurrentIndex(nextIndex);
    setShouldEagerLoad(true); // in case the hover event isn't triggered on mobile
  };

  /** Callback function to be called when swipe happens through mousevents */
  const handleSwipe = (index: number) => {
    setCurrentIndex(index);
  };

  const mlsWatermarkLogo = useMemo(() => {
    return MlsWatermarkLogoHelper.getMlsLogo(mlsName);
  }, [mlsName]);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- fixme(@retail)
    <Box
      height="100%"
      onMouseEnter={() => {
        setShouldShowArrow(true);
        setShouldEagerLoad(true);
      }}
      onMouseLeave={() => setShouldShowArrow(false)}
      data-testid="image-carousel-container"
    >
      <SwipeableViews
        enableMouseEvents
        index={currentIndex}
        onChangeIndex={handleSwipe}
        springConfig={{
          duration: '0.25s',
          easeFunction: 'cubic-bezier(0, 0, 0, 0)',
          delay: '0s',
        }}
        style={{ height: '100%', overflow: 'hidden' }}
        containerStyle={{ height: '100%' }}
        slideStyle={{ height: '100%', overflow: 'hidden' }}
      >
        {photos.map((img, index) => (
          <Box as="span" key={`${img}-${index}`} style={{ position: 'relative' }}>
            <Image
              src={img}
              alt={`${propertyAddress} view ${index + 1}`}
              borderBottomLeftRadius="none"
              borderBottomRightRadius="none"
              width="100%"
              height="100%"
              data-testid={`${currentIndex}-image-carousel`}
              background="neutrals20"
              objectFit="cover"
              loading={photoIndexesToEagerLoad.includes(index) ? 'eager' : 'lazy'}
            />
            {mlsWatermarkLogo && (
              <ImgixImg
                src={mlsWatermarkLogo}
                alt={`${mlsName} logo`}
                style={{ position: 'absolute', bottom: 15, right: 12, height: '12px' }}
              />
            )}
          </Box>
        ))}
      </SwipeableViews>
      {currentIndex != 0 && (
        <CarouselArrow
          side="left"
          onChangeIndex={(e) => handleChangeIndex('left', e)}
          hover={shouldShowArrow}
        />
      )}
      {currentIndex != photoCount - 1 && (
        <CarouselArrow
          side="right"
          onChangeIndex={(e) => handleChangeIndex('right', e)}
          hover={shouldShowArrow}
        />
      )}
      <PropertyImageCarouselProgress
        currentIndex={currentIndex}
        photoCount={photoCount}
        tabIndex={0}
      />
      <Global
        styles={css`
          #arrow-left svg:focus,
          #arrow-right svg:focus {
            color: ${colors.brand50};
            border-radius: 2em;
            border: 2px solid ${colors.brand50};
            background: white;
          }
        `}
      />
    </Box>
  );
};
